import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  Trophy,
  Star,
  Zap,
  Target,
  TrendingUp,
  Heart,
  Sparkles,
  Camera,
  Video,
  Share2,
  Users,
  Calendar,
  Award
} from 'lucide-react';

// Hook per rispettare prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

interface SuccessStateProps {
  type: 'client_added' | 'video_uploaded' | 'video_published' | 'milestone_reached' | 'goal_completed' | 'monthly_target';
  title?: string;
  message?: string;
  onClose?: () => void;
  autoClose?: boolean;
  data?: any;
}

// Particelle animate per effetti speciali
const FloatingParticles: React.FC<{ count?: number; colors?: string[] }> = ({
  count = 20,
  colors = ['#FEF08A', '#FDE047', '#FACC15']
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Reduce particle count on mobile and when reduced motion is preferred
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (prefersReducedMotion) return null;

  const particleCount = isMobile ? Math.min(count, 10) : count;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array(particleCount).fill(null).map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 sm:w-1 sm:h-1 rounded-full"
          style={{
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
          animate={{
            y: [-20, -40, -20],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0, 1, 0],
            scale: [0, 1, 0]
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2
          }}
        />
      ))}
    </div>
  );
};

// Success notification con personalita specifica per ByMarke
export const SuccessNotification: React.FC<SuccessStateProps> = ({
  type,
  title,
  message,
  onClose,
  autoClose = true,
  data
}) => {
  const [show, setShow] = useState(true);
  const [confettiActive, setConfettiActive] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const getSuccessConfig = () => {
    switch (type) {
      case 'client_added':
        return {
          icon: <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Nuovo Cliente Aggiunto!',
          message: message || `${data?.clientName || 'Il cliente'} e stato aggiunto con successo alla tua dashboard.`,
          bgGradient: 'from-blue-400 to-blue-600',
          celebration: 'medium',
          emoji: ''
        };

      case 'video_uploaded':
        return {
          icon: <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Video Caricato!',
          message: message || 'Il tuo video e stato caricato e sara presto pronto per la lavorazione.',
          bgGradient: 'from-purple-400 to-purple-600',
          celebration: 'small',
          emoji: ''
        };

      case 'video_published':
        return {
          icon: <Share2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Video Pubblicato!',
          message: message || 'Il tuo contenuto e ora live e pronto per conquistare i social!',
          bgGradient: 'from-green-400 to-green-600',
          celebration: 'large',
          emoji: ''
        };

      case 'milestone_reached':
        return {
          icon: <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Milestone Raggiunto!',
          message: message || `Complimenti! Hai raggiunto un importante traguardo.`,
          bgGradient: 'from-yellow-400 to-yellow-600',
          celebration: 'large',
          emoji: ''
        };

      case 'goal_completed':
        return {
          icon: <Target className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Obiettivo Completato!',
          message: message || 'Hai completato tutti i video programmati per questo periodo.',
          bgGradient: 'from-emerald-400 to-emerald-600',
          celebration: 'large',
          emoji: ''
        };

      case 'monthly_target':
        return {
          icon: <Award className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Target Mensile Raggiunto!',
          message: message || 'Fantastico! Hai completato tutti i reel programmati per questo mese.',
          bgGradient: 'from-pink-400 to-pink-600',
          celebration: 'extra_large',
          emoji: ''
        };

      default:
        return {
          icon: <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />,
          title: title || 'Operazione Completata!',
          message: message || 'La tua azione e stata completata con successo.',
          bgGradient: 'from-gray-400 to-gray-600',
          celebration: 'small',
          emoji: ''
        };
    }
  };

  const config = getSuccessConfig();

  useEffect(() => {
    if (!prefersReducedMotion && (config.celebration === 'large' || config.celebration === 'extra_large')) {
      setConfettiActive(true);
      setTimeout(() => setConfettiActive(false), 3000);
    }

    if (autoClose) {
      const timer = setTimeout(() => {
        setShow(false);
        setTimeout(() => onClose?.(), 300);
      }, config.celebration === 'extra_large' ? 6000 : 4000);

      return () => clearTimeout(timer);
    }
  }, [config.celebration, autoClose, onClose, prefersReducedMotion]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => onClose?.(), 300);
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Confetti per celebrazioni grandi - ridotto su mobile */}
          {confettiActive && !prefersReducedMotion && (
            <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
              {Array(30).fill(null).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 sm:w-3 sm:h-3 rounded-full"
                  style={{
                    backgroundColor: ['#FEF08A', '#FDE047', '#FACC15', '#EAB308'][Math.floor(Math.random() * 4)],
                    left: `${50 + (Math.random() - 0.5) * 30}%`,
                    top: '50%'
                  }}
                  initial={{ scale: 0, y: 0, x: 0, rotate: 0 }}
                  animate={{
                    scale: [0, 1, 0],
                    y: [0, -200 - Math.random() * 100],
                    x: [(Math.random() - 0.5) * 400],
                    rotate: [0, 360 * 3]
                  }}
                  transition={{
                    duration: 3,
                    ease: 'easeOut',
                    delay: Math.random() * 0.5
                  }}
                />
              ))}
            </div>
          )}

          {/* Main Success Modal */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-2xl p-5 sm:p-8 max-w-[95vw] sm:max-w-md w-full text-center shadow-2xl overflow-hidden"
              initial={prefersReducedMotion ? { opacity: 1 } : { scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { scale: 0.8, y: 50, opacity: 0 }}
              transition={prefersReducedMotion ? { duration: 0.1 } : {
                type: 'spring',
                damping: 20,
                stiffness: 300,
                delay: 0.1
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {/* Background Particles */}
              <FloatingParticles />

              {/* Icon with Animation */}
              <motion.div
                className={`mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r ${config.bgGradient} rounded-full flex items-center justify-center mb-4 sm:mb-6 relative z-10`}
                initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : {
                  delay: 0.3,
                  type: 'spring',
                  damping: 15,
                  stiffness: 300
                }}
              >
                {config.icon}

                {/* Pulse effect - disabled with reduced motion */}
                {!prefersReducedMotion && (
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-r ${config.bgGradient} rounded-full`}
                    animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
              </motion.div>

              {/* Title */}
              <motion.h3
                className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 sm:mb-3"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.4 }}
              >
                {config.title}
              </motion.h3>

              {/* Message */}
              <motion.p
                className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-5 sm:mb-6 leading-relaxed px-2 sm:px-0"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.5 }}
              >
                {config.message}
              </motion.p>

              {/* Action Button - touch friendly */}
              <motion.button
                className={`bg-gradient-to-r ${config.bgGradient} text-white px-6 sm:px-8 py-3 sm:py-3 min-h-[44px] rounded-xl font-medium hover:shadow-lg active:scale-95 transition-all relative z-10 touch-manipulation w-full sm:w-auto`}
                onClick={handleClose}
                whileHover={prefersReducedMotion ? {} : { scale: 1.05, y: -2 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.6 }}
              >
                Perfetto! {config.emoji}
              </motion.button>

              {/* Close button - larger touch target */}
              <button
                className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10 p-2 -m-2 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={handleClose}
                aria-label="Chiudi"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Progress Achievement Component
export const ProgressAchievement: React.FC<{
  show: boolean;
  progress: number;
  target: number;
  title: string;
  onClose: () => void;
}> = ({ show, progress, target, title, onClose }) => {
  const percentage = Math.round((progress / target) * 100);
  const isComplete = progress >= target;
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed bottom-20 sm:bottom-4 right-2 sm:right-4 left-2 sm:left-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 sm:p-6 max-w-[calc(100vw-16px)] sm:max-w-sm border border-yellow-200 dark:border-yellow-800 z-50"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: 300, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 300, scale: 0.8 }}
          transition={prefersReducedMotion ? { duration: 0.1 } : { type: 'spring', damping: 20 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base pr-2">{title}</h4>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 -m-2 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Chiudi"
            >
              <span className="text-xl">x</span>
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-gray-600 dark:text-gray-400">Progresso</span>
              <span className={`font-medium ${
                isComplete ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {progress}/{target} ({percentage}%)
              </span>
            </div>

            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 sm:h-3 overflow-hidden">
              <motion.div
                className={`h-full rounded-full bg-gradient-to-r ${
                  isComplete
                    ? 'from-green-400 to-green-500'
                    : 'from-yellow-400 to-yellow-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(percentage, 100)}%` }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 1, ease: 'easeOut' }}
              />
            </div>

            {isComplete && (
              <motion.div
                className="flex items-center justify-center space-x-2 text-green-600"
                initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: prefersReducedMotion ? 0 : 1 }}
              >
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm font-medium">Obiettivo raggiunto!</span>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Quick Success Toast
export const QuickSuccessToast: React.FC<{
  show: boolean;
  message: string;
  icon?: React.ReactNode;
  onClose: () => void;
}> = ({ show, message, icon, onClose }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed top-4 sm:top-4 left-2 right-2 sm:left-auto sm:right-4 mx-auto sm:mx-0 bg-gradient-to-r from-green-400 to-green-500 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-lg flex items-center space-x-3 z-50 max-w-[calc(100vw-16px)] sm:max-w-sm"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -50, scale: 0.9 }}
          transition={prefersReducedMotion ? { duration: 0.1 } : { type: 'spring', damping: 20 }}
        >
          <motion.div
            initial={prefersReducedMotion ? {} : { scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.1, type: 'spring' }}
            className="flex-shrink-0"
          >
            {icon || <CheckCircle className="w-5 h-5 sm:w-5 sm:h-5" />}
          </motion.div>
          <span className="font-medium text-sm sm:text-base flex-1 min-w-0 truncate">{message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
